import React, { MouseEvent, useCallback, useMemo, useState } from "react";

import { useWatch } from "antd/es/form/Form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Tooltip from "antd/es/tooltip";
import { isBefore, isEqual, isValid, parse, startOfDay } from "date-fns";
import { defineMessages, useIntl } from "react-intl";

import { Dropdown, FormItem, TimePicker, TimePickerProps } from "@mapmycustomers/ui";

import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";

import FormValues from "../../types/FormValues";

import styles from "./ActivityTime.module.scss";
import ActivityEndTime from "./components/ActivityEndTime";
import useMenu from "./useMenu";

const INTERVAL_MINUTES = 15;

const messages = defineMessages({
  changeTime: {
    id: "createActivityModal.footer.scheduleActivity.changeTime",
    defaultMessage: "Change Time",
    description: "Change Time",
  },
  setTime: {
    id: "createActivityModal.footer.scheduleActivity.setStartTime",
    defaultMessage: "Set time",
    description: "Set time text",
  },
});

interface ActivityTimeProps {
  disabledTooltip?: string;
  form: FormInstance<FormValues>;
  onChange?: () => void;
}

const ActivityTime = ({ disabledTooltip, form, onChange }: ActivityTimeProps) => {
  const intl = useIntl();

  const startDate = useWatch(ActivityFieldName.START_AT, form);
  const endDate = useWatch(ActivityFieldName.END_AT, form);

  const [startOpen, setStartOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const isAllDay = !!useWatch("allDay", form);

  const handleChange = useCallback<NonNullable<TimePickerProps["onChange"]>>(
    (value) => {
      if (isAllDay) {
        form.setFieldValue("allDay", false);
      } else {
        if (endDate && isBefore(endDate, value!)) {
          form.setFieldValue(ActivityFieldName.END_AT, value);
        }
      }
      setStartOpen(false);
      onChange?.();
    },
    [endDate, form, isAllDay, onChange]
  );

  const handleStartOpenChanged = useCallback(
    (value: boolean) => {
      setTooltipVisible(false);
      setStartOpen(value);
    },
    [setStartOpen, setTooltipVisible]
  );

  const handleStartClick = useCallback(() => {
    setTooltipVisible(false);
    setStartOpen(true);
  }, []);

  const timePickerFormat = SHOULD_USE_12_HOUR_FORMAT ? "hh:mm a" : "HH:mm";

  const handleTimeChange = useCallback(
    (date: Date) => {
      form.setFieldValue(ActivityFieldName.START_AT, date);
      if (endDate && isBefore(endDate, date)) {
        form.setFieldValue(ActivityFieldName.END_AT, date);
      }
      setStartOpen(false);
    },
    [endDate, form]
  );

  const handleMenuContainerMouseDown = useCallback((e: MouseEvent) => {
    // prevent calling on blur of TimePicker when clicking on menu
    e.preventDefault();
  }, []);

  const startDateForMenu = useMemo(() => startDate ?? new Date(), [startDate]);

  const menu = useMenu(
    startOfDay(startDateForMenu),
    INTERVAL_MINUTES,
    handleTimeChange,
    startDate,
    false,
    true
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value;

      if (!value) {
        return;
      }

      const updatedDate = parse(value, timePickerFormat, startDateForMenu);

      if (startDate && isEqual(updatedDate, startDate)) {
        return;
      }

      if (isValid(updatedDate)) {
        setTimeout(() => {
          handleTimeChange(updatedDate);
        });
      }
    },
    [handleTimeChange, startDate, startDateForMenu, timePickerFormat]
  );

  return (
    <>
      <div className={styles.group}>
        {!isAllDay && (
          <Tooltip
            destroyTooltipOnHide
            mouseLeaveDelay={0}
            onOpenChange={setTooltipVisible}
            open={tooltipVisible}
            overlayClassName={styles.tooltip}
            placement="top"
            title={disabledTooltip ?? intl.formatMessage(messages.changeTime)}
            trigger={startOpen && !disabledTooltip ? [] : ["hover"]}
          >
            <div>
              <Dropdown
                disabled={!!disabledTooltip}
                dropdownRender={(menu) => (
                  <div onMouseDown={handleMenuContainerMouseDown}>{menu}</div>
                )}
                forceRender
                menu={menu}
                onOpenChange={handleStartOpenChanged}
                open={startOpen}
                placement="bottom"
                trigger={disabledTooltip ? [] : ["click"]}
              >
                <FormItem name={ActivityFieldName.START_AT} noStyle showLabel={false}>
                  <TimePicker
                    allowClear={false}
                    className={styles.timePicker}
                    disabled={!!disabledTooltip}
                    dropdownClassName={styles.overlay}
                    format={timePickerFormat}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onClick={disabledTooltip ? undefined : handleStartClick}
                    open
                    placeholder={intl.formatMessage(messages.setTime)}
                    showSecond={false}
                    size="middle"
                    suffixIcon={null}
                    use12Hours={SHOULD_USE_12_HOUR_FORMAT}
                  />
                </FormItem>
              </Dropdown>
            </div>
          </Tooltip>
        )}

        {!isAllDay && (
          <ActivityEndTime disabledTooltip={disabledTooltip} form={form} onChange={onChange} />
        )}
      </div>
    </>
  );
};

export default ActivityTime;
